import { useState } from 'react';
import { PanGestureHandler, type PanGestureHandlerGestureEvent } from 'react-native-gesture-handler';
import { Pressable, useColorScheme, View } from 'react-native';
import { XStack, YStack } from 'tamagui';
// Lucide replaces the hand-rolled MinusIcon/PlusIcon (identical marks;
// their 2.5 stroke moves to the call sites).
import { Minus as MinusIcon, Plus as PlusIcon } from 'lucide-react-native';

import { Text } from '@/components/text';
import { SolidSurface } from '@/components/glass-surface';
import { LocationCard } from '@/components/location-card';
import { RingGlyph } from '@/components/ring-glyph';
import { TextLink } from '@/components/text-link';
import type { useCurrentLocation } from '@/hooks/use-current-location';
import { radiusHelperText } from '@/lib/distance';
import { clampRadius, MAX_RADIUS_M, MIN_RADIUS_M, RADIUS_STEP_M } from '@/lib/radius-bounds';
import { borderWidth, darkColors, lightColors, radius, space } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

// Issue #57: replaces the raw "Search radius (meters)" numeric Input
// with a visual widget -- a dot-and-circle preview of how far the
// current radiusM actually reaches, adjustable by dragging the track
// below it or by the +/-500m step buttons.
//
// Web-only implementation for now (issue #207's own decision): a real
// map view (react-native-maps) is the native path once native ships,
// but that's not built yet -- there is no `.native.tsx` sibling
// because there is no second implementation to route between, and
// adding one now for a platform this app doesn't ship on yet would be
// speculative (this project's own "does this need to exist yet?"
// convention). This file works identically on native today (it's
// plain Views/gestures, no web-specific API), so nothing breaks in
// the meantime -- it just isn't the FINAL native experience.
//
// Drag-to-adjust is the fast path; the step buttons are the
// single-pointer accessible alternative required by WCAG 2.5.7
// (Dragging Movements) -- the exact same pattern this app's photo
// grid already established (profile-photos/index.tsx's own drag +
// arrow-button pair), reused here rather than inventing new ARIA
// slider semantics from scratch.
//
// MIN/MAX/RADIUS_STEP_M and clampRadius live in lib/radius-bounds.ts,
// not here -- this file imports tamagui, which jest can't parse (see
// that module's own doc comment), so any logic worth unit-testing
// (clampRadius) has to live somewhere jest CAN load.

// Preview circle's on-screen diameter range, in px -- not
// geographically accurate (this widget has no map projection to be
// accurate WITHIN), just real, visible proportional feedback: dragging
// further right always reads as "reaches further."
const MIN_CIRCLE_SIZE = 32;
const MAX_CIRCLE_SIZE = 140;

export interface LocationRadiusPickerProps {
  location: ReturnType<typeof useCurrentLocation>;
  radiusM: number;
  onRadiusChange: (radiusM: number) => void;
}

export function LocationRadiusPicker({ location, radiusM, onRadiusChange }: LocationRadiusPickerProps) {
  const { phase, requestLocation } = location;

  // Every non-granted state (idle/locating/denied/error) is identical
  // to what LocationCard already renders for this same screen's own
  // context -- reused rather than duplicated, since radius has no
  // meaning yet without a location to center it on.
  if (phase.phase !== 'granted') {
    return <LocationCard location={location} context="show you what's happening nearby" showUpdateLink />;
  }

  return (
    <SolidSurface style={{ padding: space[4] }}>
      <YStack gap="$3">
        <XStack alignItems="center" gap="$2">
          <View style={{ width: 8, height: 8, borderRadius: radius.full, backgroundColor: '#457c5f' }} />
          <Text color="$color" flex={1}>
            Location set
          </Text>
          <TextLink fontSize="$meta" lineHeight="$meta" onPress={() => requestLocation()}>
            Update
          </TextLink>
        </XStack>

        <RadiusCirclePicker radiusM={radiusM} onRadiusChange={onRadiusChange} />
      </YStack>
    </SolidSurface>
  );
}

function RadiusCirclePicker({
  radiusM,
  onRadiusChange,
}: {
  radiusM: number;
  onRadiusChange: (radiusM: number) => void;
}) {
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  // Measured, not assumed -- this screen's content width varies (phone
  // vs. ScreenContainer's 480px-capped desktop-web rendering), same
  // reasoning as PhotoSection's own tileSize (profile-photos/index.tsx).
  const [trackWidth, setTrackWidth] = useState(0);

  const t = (radiusM - MIN_RADIUS_M) / (MAX_RADIUS_M - MIN_RADIUS_M);
  const circleSize = MIN_CIRCLE_SIZE + t * (MAX_CIRCLE_SIZE - MIN_CIRCLE_SIZE);

  function radiusFromTrackX(x: number) {
    if (trackWidth <= 0) return;

    const clampedX = Math.max(0, Math.min(trackWidth, x));
    const fraction = clampedX / trackWidth;
    const rawRadius = MIN_RADIUS_M + fraction * (MAX_RADIUS_M - MIN_RADIUS_M);
    const stepped = Math.round(rawRadius / RADIUS_STEP_M) * RADIUS_STEP_M;
    onRadiusChange(clampRadius(stepped));
  }

  function onTrackGesture(event: PanGestureHandlerGestureEvent) {
    radiusFromTrackX(event.nativeEvent.x);
  }

  function step(direction: -1 | 1) {
    onRadiusChange(clampRadius(radiusM + direction * RADIUS_STEP_M));
  }

  const thumbX = trackWidth > 0 ? t * trackWidth : 0;

  return (
    <YStack gap="$3" alignItems="center">
      <View
        style={{
          width: '100%',
          height: MAX_CIRCLE_SIZE + 16,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <View
          style={{
            width: circleSize,
            height: circleSize,
            borderRadius: radius.full,
            borderWidth: borderWidth.control,
            borderColor: colors.actionPrimary,
            backgroundColor: colors.interactiveActiveOverlay,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <View style={{ width: 8, height: 8, borderRadius: radius.full, backgroundColor: colors.actionPrimary }} />
        </View>
      </View>

      <XStack alignItems="center" gap="$2">
        <RingGlyph size={24} />
        <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
          Search radius: {radiusHelperText(radiusM)}
        </Text>
      </XStack>

      <XStack alignItems="center" gap="$3" width="100%">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Decrease search radius by half a mile"
          disabled={radiusM <= MIN_RADIUS_M}
          onPress={() => step(-1)}
          {...hit(4)}
          style={({ pressed }) => [
            {
              width: 44,
              height: 44,
              alignItems: 'center' as const,
              justifyContent: 'center' as const,
              borderRadius: radius.full,
              opacity: radiusM <= MIN_RADIUS_M ? 0.4 : 1,
            },
            pressed ? { opacity: 0.7 } : null,
          ]}
        >
          <MinusIcon size={20} strokeWidth={1.75} color={colors.textPrimary} />
        </Pressable>

        {/* accessibilityRole="adjustable" -- a real WCAG slider role for
            screen readers, even though the drag itself isn't
            keyboard-operable on its own (the step buttons above/below
            are the WCAG 2.5.7 single-pointer alternative that also
            happens to double as this control's keyboard path). */}
        <View
          onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
          accessibilityRole="adjustable"
          accessibilityValue={{ min: MIN_RADIUS_M, max: MAX_RADIUS_M, now: radiusM }}
          accessibilityLabel="Search radius"
          style={{ flex: 1, height: 44, justifyContent: 'center' }}
        >
          <PanGestureHandler onGestureEvent={onTrackGesture} onHandlerStateChange={onTrackGesture}>
            <View style={{ height: 44, justifyContent: 'center' }}>
              <View
                style={{
                  height: 4,
                  borderRadius: radius.full,
                  backgroundColor: colors.borderDefault,
                }}
              />
              <View
                style={{
                  position: 'absolute',
                  left: thumbX - 10,
                  top: 12,
                  width: 20,
                  height: 20,
                  borderRadius: radius.full,
                  backgroundColor: colors.actionPrimary,
                }}
              />
            </View>
          </PanGestureHandler>
        </View>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Increase search radius by half a mile"
          disabled={radiusM >= MAX_RADIUS_M}
          onPress={() => step(1)}
          {...hit(4)}
          style={({ pressed }) => [
            {
              width: 44,
              height: 44,
              alignItems: 'center' as const,
              justifyContent: 'center' as const,
              borderRadius: radius.full,
              opacity: radiusM >= MAX_RADIUS_M ? 0.4 : 1,
            },
            pressed ? { opacity: 0.7 } : null,
          ]}
        >
          <PlusIcon size={20} strokeWidth={1.75} color={colors.textPrimary} />
        </Pressable>
      </XStack>
    </YStack>
  );
}
